import appointmentSelectors from 'features/appointment/services/selectors';
import { ICalendarItemData, ICalendarSchema, ICalendarViewRowItem, ICalendarViewRowSpanInfoItem, IRowSpanInfo } from 'features/appointment/services/types/calendar';
import getTimeLines, { setTime } from 'features/appointment/utils/getTimeLines';
import { flatten, get, uniq } from 'lodash';
import { Moment } from 'moment';
import { useMemo } from 'react';
import { momentTimezone } from 'utils/time';
export type ICalendarMappingResultValues = {
  rows: ICalendarViewRowItem[];
  rowsSpanInfo: ICalendarViewRowSpanInfoItem[];
  data: ICalendarItemData[]
};
const useCalendarMapping = (schema: ICalendarSchema): ICalendarMappingResultValues => {
  const data = appointmentSelectors.getCalendarItems();
  const params = appointmentSelectors.paramsMasterData();

  const rowTimesMaster = useMemo(() => {
    return getTimeLines(setTime(schema.timeHourStart, schema.timeMinuteStart), setTime(schema.timeHourEnd, schema.timeMinuteEnd), schema.distanceTimeline || 15);
  }, [schema.timeHourStart, schema.timeMinuteStart, schema.timeHourEnd, schema.timeMinuteEnd, schema.distanceTimeline]);

  const rowsSpanInfo: ICalendarViewRowSpanInfoItem[] = useMemo(() => {
    const rowsSpan = schema.headers.map(col => {
      const listFilter = data.filter(order => {
        return schema.filterAppointments(order, col);
      });
      if (listFilter.length === 0) return;
      const mapping = rowTimesMaster.map(rowTime => {
        const obSetTime = { hour: rowTime.value.get('hour'), minute: rowTime.value.get('minute'), second: 0 };
        const orderList = listFilter.filter(order => {
          const orderTimeStart = momentTimezone(order.startTime);
          const orderTimeFinish = momentTimezone(order.endTime);
          const time = orderTimeStart.clone().set(obSetTime);
          const currentNextTime = time.clone().add(schema.distanceTimeline, 'minute');
          const isBetween = orderTimeStart.isBetween(time, currentNextTime);
          const isOrderTimeFinishValid = time.isSameOrAfter(orderTimeStart) && orderTimeFinish.clone().subtract(1, 'minute').isSameOrAfter(time);
          return isBetween || isOrderTimeFinishValid;
        });
        return ({
          orderList: orderList.map(o => o.id.toString()),
          rowId: rowTime.id,
          rowValue: rowTime.value,
        });
      });

      const spans: IRowSpanInfo[] = [];
      mapping.forEach((slot, index) => {
        const prevOrders = get(mapping, [index - 1, 'orderList'], []) as string[];
        const orderList = slot.orderList ?? [];
        if (orderList.length === 0) return;
        const isContinue = orderList.some(o => prevOrders.includes(o));
        if (!isContinue) {
          spans.push({
            rowStart: { id: slot.rowId, value: slot.rowValue.clone() },
            rowEnd: { id: slot.rowId, value: slot.rowValue.clone() },
            orderIds: [...slot.orderList],
            rowId: slot.rowId,
            colId: col.id,
            rowSpan: 1,
          });
        } else {
          const current = spans.find(o => !!orderList.find(k => o.orderIds.includes(k)));
          if (current) {
            current.orderIds = uniq([...current.orderIds, ...orderList]);
            current.rowEnd = { id: slot.rowId, value: slot.rowValue.clone() };
          }
        }
      });
      return spans;
    }).filter((o): o is IRowSpanInfo[] => !!o);
    const rowsSpanInfoTemp = flatten(rowsSpan);
    const rowsSpanInfo = rowsSpanInfoTemp.map(o => ({
      ...o,
      rowSpan: (o.rowEnd.value.diff(o.rowStart.value, 'minutes') / schema.distanceTimeline) + 1
    }));

    return rowsSpanInfo;
  }, [rowTimesMaster, schema.headers, schema.distanceTimeline, data]);

  const spanInfoByColId = useMemo(() => {
    const map = new Map<string, ICalendarViewRowSpanInfoItem[]>();
    rowsSpanInfo.forEach(span => {
      const list = map.get(span.colId) ?? [];
      list.push(span);
      map.set(span.colId, list);
    });
    return map;
  }, [rowsSpanInfo]);

  const rows: ICalendarViewRowItem[] = useMemo(() => {
    return rowTimesMaster.map((rowTime) => {
      const colData = schema.headers.map((col) => {
        const listFilter = data.filter(order => {
          return schema.filterAppointments(order, col);
        });
        const colSpans = spanInfoByColId.get(col.id) ?? [];
        const rowInfo = colSpans.find(o => rowTime.id === o.rowId);
        const bookingDataFilter = listFilter.filter(o => rowInfo?.orderIds.includes(o.id.toString()));
        const obSetTime = { hour: rowTime.value.get('hour'), minute: rowTime.value.get('minute'), second: 0 };
        let rowTimeValue = null;
        if (col.type === 'date') {
          rowTimeValue = (col.value as Moment)?.clone().set(obSetTime);
        } else {
          rowTimeValue = momentTimezone(params.date).set(obSetTime);
        }

        return ({
          id: col.id,
          data: bookingDataFilter,
          rowTime: rowTimeValue,
          headerData: col,
        });
      }).filter((col) => {
        const colSpans = spanInfoByColId.get(col.id) ?? [];
        return !colSpans.some(o => o.rowSpan > 1 && (
          rowTime.value.isBetween(o.rowStart.value, o.rowEnd.value) ||
          rowTime.value.isSame(o.rowEnd.value)
        ));
      });

      return {
        id: rowTime.id,
        rowTime: rowTime.value,
        colData,
      };
    });
  }, [rowTimesMaster, spanInfoByColId, schema.headers, schema.filterAppointments, data, params]);

  return ({
    rows,
    rowsSpanInfo,
    data,
  });
};

export default useCalendarMapping;
